import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilmCard from "@/components/base/FilmCard";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";
import {
  getCategoryLabel,
  getFilmById,
  getNewsForFilm,
  getRelatedFilms,
  getSpecialsForFilm,
  formatDate,
} from "@/lib/films";
import SynopsisSection from "@/pages/detail/components/SynopsisSection";
import CharacterSection from "@/pages/detail/components/CharacterSection";
import ShareBar from "@/pages/detail/components/ShareBar";

export default function Detail() {
  const { id = "" } = useParams<{ id: string }>();
  const film = getFilmById(id);
  const { isFavorite, toggleFavorite } = useFavorites();

  useSeo({
    title: film ? `${film.title} - 剧情介绍与角色分析 | 一路向西` : "影视作品详情 | 一路向西",
    description: film
      ? `深入了解${film.title}的详细剧情、角色构成及相关影视资讯。${film.oneLine}`
      : "查看影视作品的详细剧情、角色分析与相关影视资讯。",
    keywords: film
      ? `一路向西,${film.title},剧情介绍,角色分析,${film.genres.join(",")},影视动态`
      : "一路向西,影视作品,剧情介绍,角色分析",
  });

  if (!film) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="mt-3 text-sm text-foreground-600">该作品可能已下架或链接有误，去分类页看看其他内容吧。</p>
        <Link
          to="/category/all"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
        >
          <i className="ri-compass-3-line text-base" />
          浏览全部影视
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(film.id);
  const related = getRelatedFilms(film);
  const relatedNews = getNewsForFilm(film.id);
  const relatedSpecials = getSpecialsForFilm(film.id);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: getCategoryLabel(film.category), to: `/category/${film.category}` },
          { label: film.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={film.poster}
              alt={`${film.title}海报`}
              title={`${film.title} 剧情介绍与角色分析`}
              className="aspect-[2/3] w-full object-cover object-top"
            />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(film.id)}
              className={`inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                favorited
                  ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                  : "border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-400 hover:text-accent-600"
              }`}
            >
              <i className={favorited ? "ri-star-fill text-base" : "ri-star-line text-base"} />
              {favorited ? "已收藏" : "收藏作品"}
            </button>
          </div>
          <div className="mt-4">
            <ShareBar title={film.title} />
          </div>
        </div>

        <div className="lg:col-span-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-movie-line text-sm" />
            {getCategoryLabel(film.category)} · {film.region}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            {film.title}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{film.oneLine}</p>

          <div className="mt-5 flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-accent-600">
              <i className="ri-star-smile-fill text-lg" />
              {film.rating.toFixed(1)}
              <span className="text-foreground-400">/ 10</span>
            </span>
            <span className="text-sm text-foreground-500">{film.duration}</span>
            <span className="text-sm text-foreground-500">上映于 {formatDate(film.releaseDate)}</span>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-3 rounded-lg border border-background-200 bg-background-100 p-5 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-foreground-500">导演</dt>
              <dd className="mt-1 text-sm font-medium text-foreground-900">{film.director}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-foreground-500">主演</dt>
              <dd className="mt-1 text-sm font-medium text-foreground-900">{film.cast.join(" / ")}</dd>
            </div>
            <div className="sm:col-span-3">
              <dt className="text-xs text-foreground-500">类型</dt>
              <dd className="mt-1 flex flex-wrap gap-1.5">
                {film.genres.map((genre) => (
                  <span key={genre} className="rounded-md bg-background-50 px-2 py-0.5 text-xs text-foreground-700">
                    {genre}
                  </span>
                ))}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <SynopsisSection paragraphs={film.synopsis} />
          <CharacterSection characters={film.characters} />

          {relatedNews.length > 0 && (
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-newspaper-line text-xl text-primary-600" />
                相关影视动态
              </h2>
              <ul className="mt-5 space-y-4">
                {relatedNews.map((item) => (
                  <li key={item.id} className="flex flex-col gap-1 border-b border-background-200 pb-4 last:border-b-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800">{item.tag}</span>
                      <time className="text-xs text-foreground-500">{formatDate(item.date)}</time>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <section className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-information-line text-lg text-primary-600" />
              作品信息
            </h2>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">分类</span>
                <span className="text-foreground-900">{getCategoryLabel(film.category)}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">地区</span>
                <span className="text-foreground-900">{film.region}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">年份</span>
                <span className="text-foreground-900">{film.year}</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">最近更新</span>
                <span className="text-foreground-900">{formatDate(film.updatedAt)}</span>
              </li>
            </ul>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {film.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                  {tag}
                </span>
              ))}
            </div>
          </section>

          {relatedSpecials.length > 0 && (
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-bookmark-3-line text-lg text-accent-600" />
                收录专题
              </h2>
              <ul className="mt-4 space-y-3">
                {relatedSpecials.map((topic) => (
                  <li key={topic.id}>
                    <Link
                      to={`/special/${topic.id}`}
                      className="group flex items-center justify-between gap-3 rounded-md bg-background-100 px-3 py-3 transition-colors hover:bg-background-200"
                    >
                      <span className="flex flex-col">
                        <span className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-accent-700">
                          {topic.title}
                        </span>
                        <span className="mt-0.5 text-xs text-foreground-500">{topic.subtitle}</span>
                      </span>
                      <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="rounded-lg border border-background-200 bg-primary-950 p-5 text-background-50">
            <h2 className="font-heading text-base font-semibold">继续探索</h2>
            <p className="mt-2 text-sm leading-relaxed text-primary-200">
              对这类题材感兴趣？去分类页按地区、年份与类型进一步筛选，发现更多同风格影视作品。
            </p>
            <Link
              to={`/category/${film.category}`}
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-layout-grid-line text-base" />
              浏览{getCategoryLabel(film.category)}
            </Link>
          </section>
        </aside>
      </div>

      <section className="mt-12">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">相关推荐</h2>
            <p className="mt-2 text-sm text-foreground-600">根据分类、题材与标签为你匹配的相似作品。</p>
          </div>
          <Link to={`/category/${film.category}`} className="hidden items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-600 hover:text-accent-700 sm:inline-flex">
            更多同类
            <i className="ri-arrow-right-line text-base" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {related.map((item) => (
            <FilmCard key={item.id} film={item} />
          ))}
        </div>
      </section>
    </div>
  );
}